import { Permission, AdminRole, ROLE_PERMISSIONS } from '../types/auth';

/**
 * Check if user has specific permission
 */
export const hasPermission = (userPermissions: Permission[], requiredPermission: Permission): boolean => {
  return userPermissions.includes(requiredPermission);
};

/**
 * Check if user has any of the specified permissions
 */
export const hasAnyPermission = (userPermissions: Permission[], requiredPermissions: Permission[]): boolean => {
  return requiredPermissions.some(permission => userPermissions.includes(permission));
};

/**
 * Check if user has all of the specified permissions
 */
export const hasAllPermissions = (userPermissions: Permission[], requiredPermissions: Permission[]): boolean => {
  return requiredPermissions.every(permission => userPermissions.includes(permission));
};

/**
 * Check if user has specific role
 */
export const hasRole = (userRole: AdminRole, requiredRole: AdminRole): boolean => {
  return userRole === requiredRole;
};

/**
 * Check if user has any of the specified roles
 */
export const hasAnyRole = (userRole: AdminRole, requiredRoles: AdminRole[]): boolean => {
  return requiredRoles.includes(userRole);
};

/**
 * Get permissions for a specific role
 */
export const getPermissionsForRole = (role: AdminRole): Permission[] => {
  return ROLE_PERMISSIONS[role] || [];
};

/**
 * Format API endpoint with parameters
 */
export const formatEndpoint = (endpoint: string, params: Record<string, string | number>): string => {
  let formattedEndpoint = endpoint;
  
  Object.entries(params).forEach(([key, value]) => {
    formattedEndpoint = formattedEndpoint.replace(`:${key}`, String(value));
  });
  
  return formattedEndpoint;
};

/**
 * Get role display name
 */
export const getRoleDisplayName = (role: AdminRole): string => {
  const roleNames: Record<AdminRole, string> = {
    super_admin: 'Süper Yönetici',
    operations_admin: 'Operasyon Yöneticisi',
    security_admin: 'Güvenlik Yöneticisi',
    support_admin: 'Destek Yöneticisi',
    analyst: 'Analist'
  };
  
  return roleNames[role] || role;
};

/**
 * Get role color for UI
 */
export const getRoleColor = (role: AdminRole): string => {
  const roleColors: Record<AdminRole, string> = {
    super_admin: '#ff4d4f',      // Red
    operations_admin: '#1890ff', // Blue
    security_admin: '#722ed1',   // Purple
    support_admin: '#52c41a',    // Green
    analyst: '#faad14'           // Orange
  };
  
  return roleColors[role] || '#666666';
};

/**
 * Format date for display
 */
export const formatDate = (date: Date | string): string => {
  const d = new Date(date);
  return d.toLocaleDateString('tr-TR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Format relative time
 */
export const formatRelativeTime = (date: Date | string): string => {
  const d = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'Az önce';
  if (diffMins < 60) return `${diffMins} dakika önce`;
  if (diffHours < 24) return `${diffHours} saat önce`;
  if (diffDays < 7) return `${diffDays} gün önce`;
  
  return formatDate(date);
};

/**
 * Validate email format
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate password strength
 */
export const validatePassword = (password: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (password.length < 8) {
    errors.push('Şifre en az 8 karakter olmalı');
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push('Şifre en az bir büyük harf içermeli');
  }
  
  if (!/[a-z]/.test(password)) {
    errors.push('Şifre en az bir küçük harf içermeli');
  }
  
  if (!/[0-9]/.test(password)) {
    errors.push('Şifre en az bir rakam içermeli');
  }
  
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Şifre en az bir özel karakter içermeli');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Generate random string
 */
export const generateRandomString = (length: number = 16): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  
  return result;
};

/**
 * Debounce function
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};
